import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    Toolbar,
    SaveButton,
    Button,
    required,
    useDataProvider,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { normalizeCfStreamUid, validateCfStreamUid } from './validators';

const CreateToolbar = () => {
    const navigate = useNavigate();
    return (
        <Toolbar>
            <SaveButton />
            <Button label="Cancel" onClick={() => navigate('/admin/kivoai-videos')} />
        </Toolbar>
    );
};

const StreamPreview = () => {
    const uidValue = useWatch({ name: 'uid' }) as string | undefined;
    const uid = normalizeCfStreamUid(uidValue);
    const iframeUrl = uid ? `https://iframe.videodelivery.net/${uid}` : '';

    return (
        <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
                Stream Preview
            </Typography>
            {iframeUrl ? (
                <Box
                    component="iframe"
                    src={iframeUrl}
                    title="Cloudflare Stream Preview"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    sx={{
                        width: '100%',
                        height: { xs: 240, sm: 380 },
                        border: 0,
                        borderRadius: 1,
                        backgroundColor: 'black',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: 120, sm: 140 },
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: 14,
                    }}
                >
                    Fill a valid UID to preview the stream
                </Box>
            )}
        </Grid>
    );
};

const KivoaiVideoCreateForm = () => {
    const dataProvider = useDataProvider();
    const [coachChoices, setCoachChoices] = useState<Array<any>>([]);

    useEffect(() => {
        let mounted = true;
        dataProvider
            .getList('admin/dropdown/coaches', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: '', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                if (mounted) setCoachChoices(data || []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [dataProvider]);

    return (
        <SimpleForm toolbar={<CreateToolbar />}>
            <Grid container spacing={2} sx={{ width: '100%', maxWidth: 800 }}>
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom>
                        New KivoAI Video
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput source="key" label="Key" validate={required()} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput source="title" label="Title" validate={required()} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectInput
                        source="userId"
                        label="Coach"
                        choices={coachChoices}
                        validate={required()}
                        fullWidth
                    />
                </Grid>

                <Grid size={12}>
                    <TextInput
                        source="description"
                        label="Description"
                        multiline
                        rows={3}
                        fullWidth
                    />
                </Grid>

                <Grid size={12}>
                    <TextInput
                        source="uid"
                        label="Cloudflare Stream UID"
                        validate={[required(), validateCfStreamUid]}
                        helperText="Use CF Stream video UID, e.g. xxxxxxxxxxxxxxxxxxxxxxxx"
                        fullWidth
                    />
                </Grid>

                <StreamPreview />
            </Grid>
        </SimpleForm>
    );
};

const KivoaiVideoCreate = () => (
    <Create>
        <KivoaiVideoCreateForm />
    </Create>
);

export default KivoaiVideoCreate;
